import { create } from "zustand";

/**
 * Pontos de parada da pessoa, por arquivo (id do arquivo → linhas). Ficam neste computador, por
 * projeto, como no VS Code: cada pessoa depura com os próprios pontos.
 */
interface BreakpointStore {
  projectId: string | null;
  files: Record<string, number[]>;
  load(projectId: string): void;
  set(fileId: string, lines: number[]): void;
  clear(fileId?: string): void;
}

const key = (projectId: string) => `kompil.breakpoints.${projectId}`;

function read(projectId: string): Record<string, number[]> {
  try {
    const raw = JSON.parse(localStorage.getItem(key(projectId)) ?? "{}") as unknown;
    if (!raw || typeof raw !== "object") return {};
    const out: Record<string, number[]> = {};
    for (const [id, lines] of Object.entries(raw as Record<string, unknown>)) {
      if (Array.isArray(lines)) {
        const ok = lines.filter((l): l is number => Number.isInteger(l) && l > 0).slice(0, 500);
        if (ok.length) out[id] = ok;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function save(projectId: string | null, files: Record<string, number[]>) {
  if (!projectId) return;
  try {
    if (Object.keys(files).length) localStorage.setItem(key(projectId), JSON.stringify(files));
    else localStorage.removeItem(key(projectId));
  } catch {
    // armazenamento bloqueado: os pontos valem só nesta sessão
  }
}

export const useBreakpoints = create<BreakpointStore>((set, get) => ({
  projectId: null,
  files: {},
  load: (projectId) => {
    if (get().projectId === projectId) return;
    set({ projectId, files: read(projectId) });
  },
  set: (fileId, lines) => {
    const files = { ...get().files };
    if (lines.length) files[fileId] = lines;
    else delete files[fileId];
    set({ files });
    save(get().projectId, files);
  },
  clear: (fileId) => {
    const files = fileId ? { ...get().files } : {};
    if (fileId) delete files[fileId];
    set({ files });
    save(get().projectId, files);
    // Os editores abertos tiram as marcas (ver create-editor: assina esta store).
    clearRequests.forEach((fn) => fn(fileId ?? null));
  },
}));

const clearRequests = new Set<(fileId: string | null) => void>();
/** Editores abertos ouvem "remover pontos" vindos do painel Executar. */
export function onBreakpointsCleared(fn: (fileId: string | null) => void) {
  clearRequests.add(fn);
  return () => void clearRequests.delete(fn);
}
